import React, { createContext, useContext, useEffect, useRef, useState } from "react";

/**
 * HelpKit — one source of truth for SME help, shown two ways:
 *   - the full "SME Help" tab (SmeHelp.tsx renders <HelpBody/>)
 *   - a slide-in drawer that layers ABOVE any open modal (z-index 90 > modal 60),
 *     so a reviewer can open help mid-review without losing the rule they were on.
 * Any "?" dot (<HelpDot topic="..."/>) opens the drawer scrolled to that topic.
 */

export type HelpRow = { name: string; tone?: Tone; what: string; act: string };
export type HelpSection = { id: string; title: string; intro?: string; rows?: HelpRow[] };
type Tone = "green" | "amber" | "red" | "blue" | "teal" | "slate" | "sand";

const TONE: Record<Tone, [string, string]> = {
  green: ["#e9f5ee", "#2f7a4f"], amber: ["#faedcf", "#946011"], red: ["#fbe9e7", "#b23a30"],
  blue: ["#eef2f8", "#43607e"], teal: ["#e2f1ef", "#0b5f5e"], slate: ["#eef1f5", "#586675"], sand: ["#f5f0e4", "#8a6d1c"],
};

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "start", title: "How to read this workspace",
    intro: "Each rule is tagged along independent lenses, so nothing is ambiguous. A rule can be trusted (policy) yet not measured (execution) at the same time — those are different questions. Start from Status, then use the other lenses to prioritise. Every badge below also appears on the rule card and in the details panel.",
  },
  {
    id: "decision", title: "Your decision — the two levels",
    intro: "Every decision has a GATE (what happens to the rule) and a CATEGORY (why). The category implies the gate, so you only pick one thing. A comment is required on every decision — it is what the agent reads when it revises the rule.",
    rows: [
      { name: "Approved", tone: "green", what: "Ready to enforce, no caveats.", act: "Pick “Approve — makes sense”." },
      { name: "Conditional", tone: "amber", what: "You accept the rule, but something must happen first — a correction, governed values, or a scope confirmation. It is NOT a rejection.", act: "Pick the matching “Conditional — …” category and say what must change." },
      { name: "Pending", tone: "blue", what: "You cannot decide without input from the business.", act: "Pick “Needs business confirmation” and note who must confirm." },
      { name: "Rejected", tone: "red", what: "The rule will not be enforced — wrong, duplicate, or out of scope.", act: "Pick the matching reject/skip category and say why." },
    ],
  },
  {
    id: "studio", title: "Refine with agent (Rule Studio)",
    intro: "From a rule's details, “✨ Refine with agent” sends the rule, your comment, the measured evidence, the governed values and the profiled data to the agent. It answers with what it thinks you mean, up to three options with trade-offs, and — where the evidence contradicts your assumption — a challenge explaining what is really wrong. Nothing is saved until you press Save, so you can reword and re-ask as often as you like; the conversation is remembered between asks.",
    rows: [
      { name: "What I think you mean", tone: "teal", what: "The agent's reading of your comment, with the numbers it relied on.", act: "If it misread you, reword and ask again." },
      { name: "⚑ What the evidence shows", tone: "red", what: "A challenge: the evidence disagrees with your assumption (e.g. the rule was fine; the reference data is missing).", act: "Read it before accepting an option — the fix may be elsewhere." },
      { name: "Options", tone: "blue", what: "Distinct ways to express the rule, each with a rationale and a trade-off; one is recommended.", act: "Pick the one you want, then Save." },
      { name: "You need to / needs your input", tone: "sand", what: "The option depends on something you must supply — reference values, or an answer to the agent's question.", act: "Do that first, or answer in the box and re-ask." },
    ],
  },
  {
    id: "dimension", title: "DQ dimension",
    intro: "The quality question a rule answers. Use it to review one theme at a time or to report coverage by dimension.",
    rows: [
      { name: "Completeness", tone: "green", what: "A required value is present (mandatory, conditional-mandatory, paired presence).", act: "Confirm the field is truly required in the stated scope." },
      { name: "Validity", tone: "blue", what: "The value is in an allowed set or range (value sets, ranges).", act: "Check the governed list/range is right." },
      { name: "Conformity", tone: "slate", what: "The value matches a format/pattern/length.", act: "Confirm the expected format." },
      { name: "Consistency", tone: "teal", what: "Two or more fields/records agree (comparisons, exclusions, multiple-of).", act: "Confirm the cross-field logic." },
      { name: "Integrity", tone: "amber", what: "A referenced record exists (referential existence).", act: "Confirm the relationship is required." },
      { name: "Uniqueness", tone: "sand", what: "No duplicate on the key/grain.", act: "Confirm the uniqueness grain." },
    ],
  },
  {
    id: "measurement", title: "Measurement — did it run, what did it find",
    rows: [
      { name: "Passed (clean)", tone: "green", what: "Ran on all applicable records with zero violations.", act: "Safe to approve/enforce." },
      { name: "Flags data", tone: "amber", what: "Ran and found violations. The count is real unless also marked a defect.", act: "Review sample fails; approve if the finding is genuine." },
      { name: "Needs reference values", tone: "sand", what: "A value-set rule whose governed list is missing (or profiled from your own data, which is circular).", act: "Supply/confirm the governed value list — the only setup a steward provides." },
      { name: "Engine can't compile yet", tone: "slate", what: "A shape the compiler doesn't emit yet (e.g. a 3-table condition + existence check).", act: "No SME action — an engineering item. Blocks nothing." },
      { name: "No matching rows", tone: "slate", what: "Ran, but the condition matched 0 records in this extract.", act: "Not applicable here — no action unless you expected matches." },
      { name: "Engine error", tone: "red", what: "An execution/grain defect, not a data problem.", act: "No SME action — engineering fix." },
    ],
  },
  {
    id: "reviewer", title: "Reviewer — objection / revise / caveat",
    intro: "The model challenges its own rules. A rule that was refined and applied is NOT the same as one with an unresolved objection — only the latter needs your judgment.",
    rows: [
      { name: "⚠ needs judgment", tone: "red", what: "Reviewer raised an issue it could NOT auto-fix (unresolved objection).", act: "Read the objection in the details; decide keep / revise / reject." },
      { name: "⚠ reviewer: drop", tone: "red", what: "Reviewer recommends dropping the rule.", act: "Confirm the drop unless you disagree." },
      { name: "✎ refined", tone: "blue", what: "Reviewer found a wording/scope issue and APPLIED a fix. Informational.", act: "Skim that the change reads right; usually safe to approve." },
      { name: "✓ reviewed", tone: "green", what: "Reviewer checked the rule and validated it with no changes.", act: "No action — highest reviewer confidence." },
    ],
  },
  {
    id: "authority", title: "Authority — where trust comes from",
    rows: [
      { name: "✓ client-confirmed", tone: "green", what: "Backed by a rule from your data dictionary.", act: "Highest trust — safe to approve." },
      { name: "✓ client-governed values", tone: "teal", what: "Checks against a governed value set your reference data supplies.", act: "Approve if measurement is clean." },
      { name: "client-governed · values pending", tone: "sand", what: "The column is client-governed but no governed values were supplied — using observed values.", act: "Supply the governed list to make it enforceable." },
      { name: "Observed evidence", tone: "slate", what: "Supported by patterns measured on your data, not an external policy.", act: "Confirm the intent before enforcing." },
    ],
  },
  {
    id: "attention", title: "Attention",
    rows: [
      { name: "⚠ likely rule defect", tone: "amber", what: "Flags an implausibly high share of rows (>80%). Almost always an over-broad or inverted rule, or a wrong reference set — NOT bad data.", act: "Fix the rule scope; do NOT report these counts as data-quality violations." },
    ],
  },
  {
    id: "trust", title: "Trust levels",
    rows: [
      { name: "Confirmed", tone: "green", what: "Client-confirmed policy, or measured true with no counter-examples.", act: "Strongest — quick-approve candidates." },
      { name: "Supported", tone: "teal", what: "Observed evidence supports it.", act: "Approve after a glance at the evidence." },
      { name: "Tentative", tone: "sand", what: "Inferred; plausible but unverified.", act: "Review intent before approving." },
      { name: "Unverified", tone: "slate", what: "No measurement or policy backing yet.", act: "Lowest priority / needs work." },
    ],
  },
];

export const TOPIC_INDEX: Record<string, string> = {
  decision: "decision", gate: "decision", assessment: "decision", comment: "decision",
  studio: "studio", refine: "studio", agent: "studio", iteration: "studio",
  dimension: "dimension", measurement: "measurement", execution: "measurement",
  reviewer: "reviewer", challenge: "reviewer", authority: "authority",
  attention: "attention", suspect: "attention", trust: "trust", reference: "measurement",
};

/* ── tiny presentational atoms ─────────────────────────────────────────── */
export const DIM_TONE: Record<string, Tone> = {
  Completeness: "green", Validity: "blue", Conformity: "slate", Consistency: "teal", Integrity: "amber", Uniqueness: "sand",
};

// Fallback family→dimension (mirrors the backend normalizer) so the dimension is correct even when the
// served rules predate the tag being stamped. Prefer the real tag when present.
const FAM_DIM: Record<string, string> = {
  mandatory: "Completeness", conditional_mandatory: "Completeness", paired_presence: "Completeness",
  code_domain: "Validity", lookup: "Validity", allowed_values: "Validity", range: "Validity", threshold: "Validity",
  format: "Conformity", regex: "Conformity", length: "Conformity",
  comparison: "Consistency", mutual_exclusion: "Consistency", conditional_exclusion: "Consistency",
  conditional_inclusion: "Consistency", formula: "Consistency", unit_conversion: "Consistency", multiple_of: "Consistency",
  referential: "Integrity", uniqueness: "Uniqueness",
};
export function dimensionOf(r: any): string {
  return r?.tags?.dimension || FAM_DIM[r?.family] || FAM_DIM[r?.constraint_family] || "Conformity";
}

/** The normalized tag badges — one renderer so the Review card, the By-field list and the
 * details panel all show an identical set. `compact` drops the lower-priority pills. */
export function RuleTagBadges({ rule, compact, skipDim }: { rule: any; compact?: boolean; skipDim?: boolean }) {
  const t = rule?.tags || {};
  const es = t.execution_state, cs = t.challenge_state, as_ = t.authority_state;
  const dimv = dimensionOf(rule);
  const out: React.ReactNode[] = [];
  if (dimv && !skipDim) out.push(<Pill key="dim" tone={DIM_TONE[dimv] || "slate"}>{dimv}</Pill>);
  if (es === "measured_flags") out.push(<Pill key="es" tone="amber">flags {fmt(rule.impact_violations ?? rule.tag_notes?.executionCount)}</Pill>);
  else if (es === "measured_clean") out.push(<Pill key="es" tone="green">passed</Pill>);
  else if (es === "not_measured_needs_values") out.push(<Pill key="es" tone="sand">needs values</Pill>);
  else if (es === "not_measured_uncompilable") out.push(<Pill key="es" tone="slate">not compiled</Pill>);
  else if (es === "not_measured_error") out.push(<Pill key="es" tone="red">engine error</Pill>);
  else if (es === "not_measured_no_rows") out.push(<Pill key="es" tone="slate">no rows</Pill>);
  if (t.suspect_overbroad) out.push(<Pill key="sus" tone="amber">⚠ likely defect</Pill>);
  if (cs === "unresolved_objection") out.push(<Pill key="cs" tone="red">⚠ needs judgment</Pill>);
  else if (cs === "recommend_drop") out.push(<Pill key="cs" tone="red">⚠ drop</Pill>);
  else if (cs === "refined") out.push(<Pill key="cs" tone="blue">✎ refined</Pill>);
  else if (!compact && cs === "validated") out.push(<Pill key="cs" tone="green">✓ reviewed</Pill>);
  if (!compact) {
    if (as_ === "client_rule_confirmed") out.push(<Pill key="as" tone="green">✓ client-confirmed</Pill>);
    else if (as_ === "values_client_governed") out.push(<Pill key="as" tone="teal">✓ client-governed</Pill>);
    else if (as_ === "column_client_governed") out.push(<Pill key="as" tone="sand">values pending</Pill>);
  }
  return <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{out}</div>;
}
function fmt(n: any) { const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : (n ?? ""); }


export function Pill({ tone = "slate", children }: { tone?: Tone; children: React.ReactNode }) {
  const [bg, fg] = TONE[tone];
  return <span style={{ background: bg, color: fg, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3 }}>{children}</span>;
}

export function HelpBody({ only }: { only?: string[] }) {
  const secs = only ? HELP_SECTIONS.filter((s) => only.includes(s.id)) : HELP_SECTIONS;
  return (
    <div className="hk-body">
      {secs.map((s) => (
        <section key={s.id} id={`hk-${s.id}`} className="hk-sec">
          <h3>{s.title}</h3>
          {s.intro && <p className="hk-intro">{s.intro}</p>}
          {s.rows && (
            <div className="hk-tbl">
              {s.rows.map((r, i) => (
                <div className="hk-tr" key={i}>
                  <div className="hk-c1"><Pill tone={r.tone}>{r.name}</Pill></div>
                  <div className="hk-c2">{r.what}</div>
                  <div className="hk-c3">{r.act}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

/* ── context + drawer ──────────────────────────────────────────────────── */
const HelpCtx = createContext<{ open: (topic?: string) => void }>({ open: () => {} });
export const useHelp = () => useContext(HelpCtx);

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [topic, setTopic] = useState<string | null>(null);
  const open = (t?: string) => setTopic(t || "start");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!topic) return;
    const sec = TOPIC_INDEX[topic] || topic;
    const t = setTimeout(() => {
      const el = document.getElementById(`hk-${sec}`);
      if (el && ref.current) ref.current.scrollTo({ top: el.offsetTop - 8, behavior: "smooth" });
    }, 60);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setTopic(null);
    window.addEventListener("keydown", esc);
    return () => { clearTimeout(t); window.removeEventListener("keydown", esc); };
  }, [topic]);

  return (
    <HelpCtx.Provider value={{ open }}>
      <style>{CSS}</style>
      {children}
      {/* persistent floating button */}
      <button className="hk-fab" title="SME help" onClick={() => open("start")}>?</button>
      {topic && (
        <div className="hk-scrim" onClick={() => setTopic(null)}>
          <aside className="hk-drawer" ref={ref} onClick={(e) => e.stopPropagation()}>
            <header className="hk-head">
              <div>
                <div className="hk-eyebrow">SME HELP</div>
                <div className="hk-title">What every tag means</div>
              </div>
              <button className="hk-x" onClick={() => setTopic(null)}>×</button>
            </header>
            <HelpBody />
            <div className="hk-foot">Press <kbd>Esc</kbd> to close · your rule stays open underneath</div>
          </aside>
        </div>
      )}
    </HelpCtx.Provider>
  );
}

/** small "?" affordance that opens the drawer to a topic without closing the modal */
export function HelpDot({ topic, label }: { topic: string; label?: string }) {
  const { open } = useHelp();
  return (
    <button className="hk-dot" title={label || "What does this mean?"} onClick={(e) => { e.stopPropagation(); open(topic); }}>?</button>
  );
}

const CSS = `
.hk-dot{all:unset;cursor:pointer;display:inline-grid;place-items:center;width:15px;height:15px;border-radius:50%;
 background:#e7ebf0;color:#5a6675;font-size:10px;font-weight:700;line-height:1;margin-left:5px;vertical-align:middle}
.hk-dot:hover{background:#0e7c7b;color:#fff}
.hk-fab{position:fixed;right:18px;bottom:18px;z-index:70;width:44px;height:44px;border-radius:50%;border:0;cursor:pointer;
 background:#0e2436;color:#fff;font-size:20px;font-weight:700;box-shadow:0 6px 20px rgba(14,36,54,.28)}
.hk-fab:hover{background:#0e7c7b}
.hk-scrim{position:fixed;inset:0;z-index:90;background:rgba(14,36,54,.28);display:flex;justify-content:flex-end}
.hk-drawer{width:min(560px,94vw);height:100%;background:#fff;overflow-y:auto;box-shadow:-16px 0 48px rgba(0,0,0,.22);
 animation:hkslide .22s cubic-bezier(.2,.7,.3,1)}
@keyframes hkslide{from{transform:translateX(24px);opacity:.6}to{transform:none;opacity:1}}
.hk-head{position:sticky;top:0;background:#0e2436;color:#fff;display:flex;justify-content:space-between;align-items:center;padding:16px 20px;z-index:1}
.hk-eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.hk-title{font-size:17px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.hk-x{all:unset;cursor:pointer;font-size:24px;line-height:1;color:#cdd8ea;padding:0 4px}.hk-x:hover{color:#fff}
.hk-body{padding:8px 20px 20px}
.hk-sec{padding:16px 0;border-bottom:1px solid #eef1f4}
.hk-sec h3{font-size:14px;margin:0 0 8px;color:#1c2530;font-family:'Space Grotesk',system-ui,sans-serif}
.hk-intro{font-size:13px;color:#41474e;line-height:1.55;margin:0 0 10px}
.hk-tbl{display:flex;flex-direction:column;gap:1px;background:#eef1f4;border:1px solid #eef1f4;border-radius:8px;overflow:hidden}
.hk-tr{display:grid;grid-template-columns:150px 1fr;grid-template-areas:'a b' 'a c';gap:2px 10px;background:#fff;padding:9px 11px}
.hk-c1{grid-area:a;align-self:start}
.hk-c2{grid-area:b;font-size:12.5px;color:#33404e;line-height:1.45}
.hk-c3{grid-area:c;font-size:12px;color:#0b5f5e;line-height:1.4}
.hk-c3::before{content:'→ ';opacity:.6}
.hk-foot{position:sticky;bottom:0;background:#f6f8fa;border-top:1px solid #eef1f4;padding:9px 20px;font-size:11.5px;color:#6b7c8d}
.hk-foot kbd{background:#fff;border:1px solid #d8dee6;border-radius:4px;padding:0 5px;font-family:inherit;font-size:11px}
`;
